import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client'
import {
  CheckEligibilityInput,
  EligibilityResponse,
  IneligibilityReason,
  Insurer,
  MutationCheckEligibilityArgs,
  Patient,
  ProfileInformation,
  UserRegistrationInput,
} from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import RadioGroupMedkit from '@babylon/medkit/src/Forms/Radios/RadioGroupV2'
import { logException } from '@babylon/sentry'

import { Heading, Radio, RadioGroup, Spacer } from '@babylon/web-platform-ui'
import defaultFields, { SELECT_FIELD } from '@/config/premadeConfigs/field'
import { FormRenderer, Loading } from '@/components'
import InsurerPlansQuery from '@/queries/InsurerPlans'
import CheckEligibilityMutation from '@/mutations/CheckEligibility'
import UpdateUserMutation from '@/mutations/updateUser'
import { useConsumerNetworkUuid } from '@/redux/userConfig/selectors'
import { showError, showSuccess } from '@/redux/appMessage/actions'
import { trackTealiumEvent, trackTealiumView } from '@/tracking'
import { tealiumEligibilityScreenSections } from '@/constants'
import { useTrackingScreenTitle } from '@/hooks'
import { replaceString } from '@/utils'
import { Field } from '@/types/partnerConfig'
import { InsurerPlansResponse } from '@/types'
import messages from './messages'
import errorMessages from './error.messages'
import HelpText from './HelpText'
import {
  stripNonAcceptedFields,
  userUpdateFields,
} from '@/hooks/useRegisterUser'
import appStyles from '@/App.module.scss'
import formMessages from '../FormRenderer/messages'
import { useConfig } from '@/redux/partnerConfig/selectors'

interface Props {
  patient: Patient
  personType: string
  insurers: Insurer[]
  onComplete: () => void
  onUpdateScreenSection: (ScreenSection: string) => void
}

type FormVariables = CheckEligibilityInput &
  Pick<Patient, 'first_name' | 'last_name' | 'date_of_birth'> & {
    showInsuranceProfileUpdate?: string
  }

type CheckEligibilityResponse = {
  checkEligibility: EligibilityResponse
}

type UpdateUserResponse = {
  updateUser: Patient
}

type UpdateUserArgs = {
  id: string
  patient: ProfileInformation
}

const EligibilityForm = ({
  patient,
  personType,
  insurers,
  onComplete,
  onUpdateScreenSection,
}: Props) => {
  const translate = useFormatMessage()
  const dispatch = useDispatch()
  const { useDnaV2Design } = useConfig()
  const stepTrackingName = useTrackingScreenTitle()
  const [insurerId, setInsurerId] = useState<number>()
  const [planUuid, setPlanUuid] = useState<string>()
  const [loading, setLoading] = useState<boolean>(false)
  const [
    showInsuranceProfileUpdate,
    setShowInsuranceProfileUpdate,
  ] = useState<boolean>()

  const {
    ADD_POLICY,
    AMEND_DETAILS,
    CHECK_INSURANCE,
  } = tealiumEligibilityScreenSections
  const consumerNetworkUuid = useConsumerNetworkUuid()
  const [activeScreenSection, setActiveScreenSection] = useState<string>(
    CHECK_INSURANCE
  )

  const {
    data: { insurerPlans = [] } = {},
    loading: insurerPlansLoading,
  } = useQuery<InsurerPlansResponse>(InsurerPlansQuery, {
    variables: { insurerId },
    skip: !insurerId,
  })

  const [checkEligibility] = useMutation<CheckEligibilityResponse>(
    CheckEligibilityMutation
  )

  const [updateUser] = useMutation<UpdateUserResponse, UpdateUserArgs>(
    UpdateUserMutation
  )

  useEffect(() => {
    if (insurers?.length === 1) {
      setInsurerId(Number(insurers[0].id))
    }
  }, [insurers])

  useEffect(() => {
    if (insurerPlans?.length === 1) {
      setPlanUuid(insurerPlans[0].uuid)
    }
  }, [insurerPlans])

  useEffect(() => {
    // This TackView has to fire always before the state of the form changes
    trackTealiumView({
      screen_title: stepTrackingName,
      screenSection: CHECK_INSURANCE,
      recipientId: patient?.id,
      tealium_event: stepTrackingName,
    })
    onUpdateScreenSection(CHECK_INSURANCE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onScreenSectionChange = (screenSection: string) => {
    onUpdateScreenSection(screenSection)
    setActiveScreenSection(screenSection)
    trackTealiumView({
      screen_title: stepTrackingName,
      screenSection,
      recipientId: patient?.id,
      tealium_event: stepTrackingName,
    })
  }

  const intlParams = {
    name: patient.first_name,
    person: personType,
  }

  const insurersDropdown: Field = {
    ...SELECT_FIELD,
    name: 'insurerId',
    props: {
      placeholder: translate(messages.insuranceProviderPlaceholder, intlParams),
      options: insurers?.map((insurer) => ({
        label: insurer.name,
        value: insurer.id,
      })),
    },
  }
  const insurerPlansDropdown: Field = {
    ...SELECT_FIELD,
    name: 'planUuid',
    disabled: insurerPlans.length === 0,
    props: {
      placeholder: translate(messages.insurancePlanPlaceholder, intlParams),
      options: insurerPlans.map((plan) => ({
        label: plan.name,
        value: plan.uuid,
      })),
      endAdornment: insurerPlansLoading ? <Loading inline /> : undefined,
    },
  }

  const insuranceIdField: Field = {
    name: 'externalInsuranceId',
    type: 'text',
    required: true,
    disabled: false,
    label: translate(formMessages.externalInsuranceId, intlParams),
    helpText: translate(messages.insuranceIdHelpText, intlParams),
  }

  const insurerFields: Field[] = [
    insurersDropdown,
    insurerPlansDropdown,
    insuranceIdField,
  ]

  const profileFields: Field[] = [
    defaultFields.FIRST_NAME,
    defaultFields.LAST_NAME,
    {
      ...defaultFields.DATE_OF_BIRTH,
      props: {
        maxDate: new Date(),
      },
    },
    ...insurerFields,
  ]

  const initialInsurerValues: CheckEligibilityInput = {
    patientId: patient.id,
    consumerNetworkUuid,
    insurerId,
    planUuid,
  }

  const initialProfileValues: FormVariables = {
    ...initialInsurerValues,
    first_name: patient.first_name,
    last_name: patient.last_name,
    date_of_birth: patient.date_of_birth,
  }

  const onValuesChange = (
    previousValues: FormVariables,
    values: FormVariables
  ) => {
    if (values.insurerId) {
      setInsurerId(values.insurerId)
    }

    if (values.planUuid) {
      setPlanUuid(values.planUuid)
    }
  }

  const onRadioChange = (value: string | string[]) => {
    if (value === 'yes') {
      // trigger screen view
      setShowInsuranceProfileUpdate(false)
      onScreenSectionChange(ADD_POLICY)
    } else if (value === 'no') {
      setShowInsuranceProfileUpdate(true)
      onScreenSectionChange(AMEND_DETAILS)
    }

    if (typeof value === 'string') {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'identity',
        eventAction: 'select are details correct',
        eventLabel: value,
        recipientId: patient.id,
        screenSection: activeScreenSection,
        tealium_event: 'select are details correct',
      })
    }
  }

  const displayErrors = (error?: IneligibilityReason | null) => {
    if (error) {
      const message = translate(errorMessages[error], intlParams)
      dispatch(showError(message))
    }
  }

  const onSubmit = async (params: FormVariables) => {
    setLoading(true)

    const { first_name, last_name, date_of_birth, ...eligibilityInput } = params

    const eligibilityParams: MutationCheckEligibilityArgs = {
      input: {
        ...eligibilityInput,
      },
    }

    try {
      if (showInsuranceProfileUpdate) {
        const strippedDownProfile = stripNonAcceptedFields(
          patient as UserRegistrationInput,
          userUpdateFields
        )
        await updateUser({
          variables: {
            id: patient.id,
            patient: {
              ...strippedDownProfile,
              first_name,
              last_name,
              date_of_birth,
            },
          },
        })
      }

      const { data } = await checkEligibility({
        variables: eligibilityParams,
      })

      const eligibilityResponse = data?.checkEligibility

      if (eligibilityResponse?.eligible) {
        const message = translate(messages.success, intlParams)
        dispatch(showSuccess(message))

        trackTealiumEvent({
          screen_title: stepTrackingName,
          eventCategory: 'identity',
          eventAction: 'completed eligibility check',
          recipientId: patient?.id,
          tealium_event: 'completed eligibility check',
        })

        onComplete()
      } else {
        const errorType = eligibilityResponse?.reason?.details?.type

        if (errorType) {
          displayErrors(errorType)
          trackTealiumEvent({
            screen_title: stepTrackingName,
            eventCategory: 'identity',
            eventAction: 'eligibility error',
            eventLabel: replaceString(errorType, '_', ' ').join(''),
            screenSection: activeScreenSection,
            recipientId: patient.id,
            tealium_event: 'eligibility error',
          })
        }
      }
    } catch (e) {
      logException(e)
    }

    setLoading(false)
  }

  const formRendererProps: Omit<
    React.ComponentProps<typeof FormRenderer>,
    'fields' | 'initialValues'
  > = {
    testId: 'welcome-page-form',
    onValuesChange,
    onSubmit,
    submitButtonText: translate(messages.continue, intlParams),
    loading,
  }
  const radioOptions = [
    {
      label: translate(messages.yes),
      value: 'yes',
    },
    {
      label: translate(messages.no),
      value: 'no',
    },
  ]

  return (
    <div className={appStyles.spaceLargeVertical}>
      {useDnaV2Design && (
        <>
          <Heading as="h4" size="xxs">
            {translate(messages.showInsuranceProfileUpdate, intlParams)}
          </Heading>
          <Spacer marginTop="1" />
          <HelpText patient={patient} />
          <Spacer marginTop="1" />
          <RadioGroup
            name="showInsuranceProfileUpdate"
            onChange={onRadioChange}
          >
            {radioOptions.map((o) => (
              <Radio key={o.label} label={o.label} value={o.value} />
            ))}
          </RadioGroup>
          <Spacer marginTop="5" />
        </>
      )}
      {!useDnaV2Design && (
        <RadioGroupMedkit
          name="showInsuranceProfileUpdate"
          legend={translate(messages.showInsuranceProfileUpdate, intlParams)}
          options={radioOptions}
          onChange={onRadioChange}
          helpText={<HelpText patient={patient} />}
        />
      )}
      {showInsuranceProfileUpdate === true && (
        <FormRenderer
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formRendererProps}
          fields={profileFields}
          initialValues={initialProfileValues}
        />
      )}
      {showInsuranceProfileUpdate === false && (
        <FormRenderer
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formRendererProps}
          fields={insurerFields}
          initialValues={initialInsurerValues}
        />
      )}
    </div>
  )
}

export default EligibilityForm
